import {useEffect, useState} from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

import SoftBox from "components/SoftBox";
import DashboardLayout from "../../custom/LayoutContainers/DashboardLayout";
import SoftTypography from "../../components/SoftTypography";
import SoftBadge from "../../components/SoftBadge";
import SoftAvatar from "../../components/Avatar";
import SoftButton from "../../components/SoftButton";
import {ChildCare, Apartment, Work, Shield} from "@mui/icons-material";
import {useRayzayUIController} from "../../context";
import {getMyChildren, getChildImages, getAllChildren} from "../../api/children/childrenApiClient";
import {getAllBranches} from "../../api/branch/branchApiClient";
import {getAllStaff} from "../../api/staff/staffApiClient";
import {getAllGuardians} from "../../api/guardian/guardianApiClient";

// Small reusable stat tile for the admin dashboard.
function StatTile({label, value, icon, color, loading}) {
    return (
        <Card
            sx={({palette}) => ({
                p: 2.5,
                height: "100%",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 2,
                borderRadius: 3,
                border: `1px solid ${palette.grey[200]}`,
                transition: "transform 150ms ease, box-shadow 150ms ease",
                "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: palette.boxShadows?.md,
                },
            })}
        >
            <SoftBox
                sx={({palette}) => ({
                    width: 56,
                    height: 56,
                    borderRadius: "14px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: `linear-gradient(135deg, ${palette[color].main} 0%, ${palette.gradients[color].state} 100%)`,
                    color: palette.white.main,
                    flexShrink: 0,
                    "& svg": {fontSize: 28},
                })}
            >
                {icon}
            </SoftBox>
            <SoftBox
                sx={{
                    minWidth: 0,
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: 0.25,
                }}
            >
                <SoftTypography
                    variant="h3"
                    fontWeight="bold"
                    color="dark"
                    sx={{lineHeight: 1, letterSpacing: "-0.02em"}}
                >
                    {loading ? "—" : value}
                </SoftTypography>
                <SoftTypography
                    variant="caption"
                    fontWeight="bold"
                    color="text"
                    sx={{
                        lineHeight: 1.2,
                        letterSpacing: 0.6,
                        textTransform: "uppercase",
                        opacity: 0.7,
                    }}
                >
                    {label}
                </SoftTypography>
            </SoftBox>
        </Card>
    );
}

StatTile.defaultProps = {
    loading: false,
};

StatTile.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    icon: PropTypes.node.isRequired,
    color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error"]).isRequired,
    loading: PropTypes.bool,
};

function Dashboard() {
    const [controller] = useRayzayUIController();
    const {loggedInUserDetail} = controller;

    const [children, setChildren] = useState([]);
    const [childrenCount, setChildrenCount] = useState(0);
    const [loadingChildren, setLoadingChildren] = useState(false);
    const [childrenError, setChildrenError] = useState("");
    const [selectedChild, setSelectedChild] = useState(null);
    const [childDialogOpen, setChildDialogOpen] = useState(false);
    const [childImages, setChildImages] = useState([]);
    const [loadingChildImages, setLoadingChildImages] = useState(false);
    const [childImagesError, setChildImagesError] = useState("");

    useEffect(() => {
        const loadChildrenForGuardian = async () => {
            if (!loggedInUserDetail || loggedInUserDetail.userRole !== "GUARDIAN") return;
            try {
                setLoadingChildren(true);
                setChildrenError("");

                const data = await getMyChildren();
                const list = Array.isArray(data?.children)
                    ? data.children
                    : Array.isArray(data)
                        ? data
                        : [];

                setChildren(list || []);
                const apiCount = typeof data?.count === "number" ? data.count : list.length;
                setChildrenCount(apiCount);
            } catch (err) {
                console.error("Failed to load children for guardian dashboard:", err);
                setChildren([]);
                setChildrenError(err.message || "Failed to load children");
            } finally {
                setLoadingChildren(false);
            }
        };

        loadChildrenForGuardian();
    }, [loggedInUserDetail]);

    const isGuardian = loggedInUserDetail?.userRole === "GUARDIAN";

    // Admin-side aggregate counts for the stat tiles.
    const [stats, setStats] = useState({children: null, branches: null, staff: null, guardians: null});
    const [statsLoading, setStatsLoading] = useState(false);

    useEffect(() => {
        if (!loggedInUserDetail || isGuardian) return;
        let cancelled = false;
        setStatsLoading(true);
        const countOf = (resp) => {
            if (Array.isArray(resp?.data)) return resp.data.length;
            if (Array.isArray(resp)) return resp.length;
            return 0;
        };
        Promise.allSettled([
            getAllChildren(),
            getAllBranches(),
            getAllStaff(),
            getAllGuardians(),
        ])
            .then(([c, b, s, g]) => {
                if (cancelled) return;
                setStats({
                    children: c.status === "fulfilled" ? countOf(c.value) : 0,
                    branches: b.status === "fulfilled" ? countOf(b.value) : 0,
                    staff: s.status === "fulfilled" ? countOf(s.value) : 0,
                    guardians: g.status === "fulfilled" ? countOf(g.value) : 0,
                });
            })
            .finally(() => {
                if (!cancelled) setStatsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [loggedInUserDetail, isGuardian]);

    const handleOpenChildDialog = async (child) => {
        setSelectedChild(child);
        setChildDialogOpen(true);
        setChildImages([]);
        setChildImagesError("");

        // If images are already included in payload, use them
        if (child.images && Array.isArray(child.images) && child.images.length > 0) {
            setChildImages(child.images);
            return;
        }

        const childId = child.childId || child.id;
        if (!childId) return;

        try {
            setLoadingChildImages(true);
            const imagesResponse = await getChildImages(childId);
            let imagesData = [];
            if (Array.isArray(imagesResponse)) {
                imagesData = imagesResponse;
            } else if (imagesResponse && Array.isArray(imagesResponse.data)) {
                imagesData = imagesResponse.data;
            }
            setChildImages(imagesData || []);
        } catch (err) {
            console.error("Failed to load child images:", err);
            setChildImages([]);
            // Show a clearer message when access is denied
            // (e.g. guardians not allowed to see certain photos)
            const status = err?.response?.status;
            if (status === 403) {
                setChildImagesError(
                    "Access denied. You don\u2019t have permission to view photos for this child."
                );
            } else {
                setChildImagesError(err.message || "Failed to load images");
            }
        } finally {
            setLoadingChildImages(false);
        }
    };

    const handleCloseChildDialog = () => {
        setChildDialogOpen(false);
        setSelectedChild(null);
        setChildImages([]);
        setChildImagesError("");
    };

    return (
        <DashboardLayout>
            <SoftBox mb={3} width="100%">
                <SoftBox pt={2} px={2} width="100%">
                    <SoftBox
                        pt={2}
                        px={2}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                    >
                        <SoftBox>
                            <SoftTypography variant="h3" fontWeight="bold" color="dark" sx={{letterSpacing: "-0.02em"}}>
                                {loggedInUserDetail?.firstName
                                    ? `Welcome back, ${loggedInUserDetail.firstName}`
                                    : "Welcome"}
                            </SoftTypography>
                            <SoftTypography variant="body1" color="text" sx={{mt: 0.5}}>
                                {isGuardian
                                    ? "Here's a snapshot of your children."
                                    : "Here's what's happening across the platform today."}
                            </SoftTypography>
                        </SoftBox>
                    </SoftBox>
                </SoftBox>
                <SoftBox p={2} width="100%">
                    <Grid container spacing={3}>
                      {!isGuardian && (
                        <>
                            <Grid item xs={12} sm={6} md={3}>
                                <StatTile
                                    label="CHILDREN"
                                    value={stats.children ?? 0}
                                    icon={<ChildCare/>}
                                    color="primary"
                                    loading={statsLoading}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <StatTile
                                    label="BRANCHES"
                                    value={stats.branches ?? 0}
                                    icon={<Apartment/>}
                                    color="warning"
                                    loading={statsLoading}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <StatTile
                                    label="STAFF"
                                    value={stats.staff ?? 0}
                                    icon={<Work/>}
                                    color="success"
                                    loading={statsLoading}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <StatTile
                                    label="GUARDIANS"
                                    value={stats.guardians ?? 0}
                                    icon={<Shield/>}
                                    color="info"
                                    loading={statsLoading}
                                />
                            </Grid>
                        </>
                      )}

                        {isGuardian && (
                            <Grid item xs={12} md={6}>
                                <Card
                                    sx={{
                                        height: "100%",
                                        p: 2,
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 2,
                                    }}
                                >
                                    <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <SoftTypography variant="h6" fontWeight="medium">
                                            Your Children
                                        </SoftTypography>
                                        <SoftBadge
                                            color="info"
                                            size="sm"
                                            badgeContent={`${childrenCount} connected`}
                                        />
                                    </SoftBox>

                                    {loadingChildren ? (
                                        <SoftTypography variant="body2" color="text.secondary">
                                            Loading your children...
                                        </SoftTypography>
                                    ) : childrenError ? (
                                        <SoftTypography variant="body2" color="error">
                                            {childrenError}
                                        </SoftTypography>
                                    ) : children.length === 0 ? (
                                        <SoftTypography variant="body2" color="text.secondary">
                                            No children are currently linked to your account.
                                        </SoftTypography>
                                    ) : (
                                        <SoftBox
                                            component="ul"
                                            p={0}
                                            m={0}
                                            sx={{
                                                listStyle: "none",
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 2,
                                            }}
                                        >
                                            {children.map((child) => (
                                                <SoftBox
                                                    key={child.childId || child.id}
                                                    component="li"
                                                    display="flex"
                                                    flexDirection="column"
                                                    alignItems="center"
                                                    sx={{cursor: "pointer", minWidth: 90}}
                                                    onClick={() => handleOpenChildDialog(child)}
                                                >
                                                    <SoftAvatar
                                                        src={child.profilePic ? `data:image/png;base64,${child.profilePic}` : undefined}
                                                        alt={`${child.firstName} ${child.lastName || ""}`}
                                                        variant="rounded"
                                                        shadow="md"
                                                        sx={{
                                                            mb: 0.5,
                                                            width: 56,
                                                            height: 56,
                                                            border: "2px solid",
                                                            borderColor:
                                                                child.gender === "MALE"
                                                                    ? "#1976d2" // blue for boys
                                                                    : child.gender === "FEMALE"
                                                                        ? "#e91e63" // pink for girls
                                                                        : "transparent",
                                                        }}
                                                    />
                                                    <SoftTypography
                                                        variant="caption"
                                                        fontWeight="medium"
                                                        textAlign="center"
                                                    >
                                                        {child.firstName}
                                                    </SoftTypography>
                                                    <SoftTypography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        textAlign="center"
                                                    >
                                                        {child.gender === "MALE"
                                                            ? "Boy"
                                                            : child.gender === "FEMALE"
                                                                ? "Girl"
                                                                : child.gender || ""}
                                                    </SoftTypography>
                                                </SoftBox>
                                            ))}
                                        </SoftBox>
                                    )}
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                </SoftBox>
            </SoftBox>

            {isGuardian && selectedChild && (
                <Dialog
                    open={childDialogOpen}
                    onClose={handleCloseChildDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            pr: 1,
                        }}
                    >
                        <SoftTypography variant="h6" fontWeight="medium">
                            Child Profile View
                        </SoftTypography>
                        <IconButton
                            size="small"
                            onClick={handleCloseChildDialog}
                            aria-label="Close"
                        >
                            <Icon>close</Icon>
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <SoftBox
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            mb={2}
                            gap={1.5}
                        >
                            <SoftAvatar
                                src={
                                    selectedChild.profilePic
                                        ? `data:image/png;base64,${selectedChild.profilePic}`
                                        : undefined
                                }
                                alt={`${selectedChild.firstName} ${selectedChild.lastName || ""}`}
                                variant="rounded"
                                shadow="md"
                                sx={{
                                    width: 120,
                                    height: 120,
                                    border: "2px solid",
                                    borderColor:
                                        selectedChild.gender === "MALE"
                                            ? "#1976d2"
                                            : selectedChild.gender === "FEMALE"
                                                ? "#e91e63"
                                                : "transparent",
                                }}
                            />
                            <SoftBox textAlign="center">
                                <SoftTypography variant="body1" fontWeight="medium" mb={0.5}>
                                    {selectedChild.firstName} {selectedChild.lastName || ""}
                                </SoftTypography>
                                <SoftTypography variant="caption" color="text.secondary">
                                    {selectedChild.gender === "MALE"
                                        ? "Boy"
                                        : selectedChild.gender === "FEMALE"
                                            ? "Girl"
                                            : selectedChild.gender || "Gender not provided"}
                                </SoftTypography>
                            </SoftBox>
                        </SoftBox>

                        <SoftBox mb={2}>
                            <Grid container spacing={1}>
                                <Grid item xs={6}>
                                    <SoftTypography variant="caption" color="text.secondary">
                                        Date of Birth
                                    </SoftTypography>
                                    <SoftTypography variant="body2">
                                        {selectedChild.dob || "Not provided"}
                                    </SoftTypography>
                                </Grid>
                                <Grid item xs={6}>
                                    <SoftTypography variant="caption" color="text.secondary">
                                        Status
                                    </SoftTypography>
                                    <SoftTypography variant="body2">
                                        {selectedChild.status || "Not provided"}
                                    </SoftTypography>
                                </Grid>
                                <Grid item xs={12}>
                                    <SoftTypography variant="caption" color="text.secondary">
                                        Branch
                                    </SoftTypography>
                                    <SoftTypography variant="body2">
                                        {selectedChild.branchName || "Not provided"}
                                    </SoftTypography>
                                </Grid>
                            </Grid>
                        </SoftBox>

                        <Divider sx={{my: 1.5}}/>
                    </DialogContent>
                    <DialogActions>
                        <SoftButton variant="text" color="text" onClick={handleCloseChildDialog}>
                            Close
                        </SoftButton>
                    </DialogActions>
                </Dialog>
            )}
        </DashboardLayout>
    );
}

export default Dashboard;
